<template>
  <header id="top" class="fixed-top shadow-sm bg-white border-bottom">
    <the-impersonate-stop />
    <div class="container-xl">
      <div class="d-flex align-items-center justify-content-between py-8" style="min-height: 52px;">
        <div class="d-flex align-items-center">
          <div class="d-flex flex-column align-items-start me-32">
            <a class="d-flex align-items-center text-decoration-none" style="cursor: pointer;" @click.prevent="$router.push({ name: 'supplierPortal.upcoming' })">
              <img alt="WAW Logo" src="https://site.waw.travel/images/wawlogo.svg" style="height: 32px; width: auto;"/>
            </a>
          </div>
          
          <nav class="d-none d-md-flex align-items-center gap-4 me-32">
            <router-link 
              :to="{ name: 'supplierPortal.upcoming', query: $route.query }" 
              class="nav-link px-8 py-4 rounded-3 fw-bold text-sm transition-all"
              :class="$route.name === 'supplierPortal.upcoming' ? 'bg-primary text-white shadow-sm' : 'text-muted'"
            >
              {{ $t('supplier.portal.upcoming') }}
            </router-link>
            <router-link 
              :to="{ name: 'supplierPortal.past', query: $route.query }" 
              class="nav-link px-8 py-4 rounded-3 fw-bold text-sm transition-all"
              :class="$route.name === 'supplierPortal.past' ? 'bg-primary text-white shadow-sm' : 'text-muted'"
            >
              {{ $t('supplier.portal.past') }}
            </router-link>
            <a 
              v-if="isAdmin"
              :href="$router.resolve({ name: 'supplier.index' }).href"
              class="nav-link px-8 py-4 rounded-3 fw-bold text-sm transition-all text-muted"
            >
              <v-icon icon="arrow-left" size="xs" class="me-4" />
              Back to Admin
            </a>
          </nav>
        </div>

        <div class="d-flex align-items-center gap-20">
          <div class="d-none d-md-flex flex-column align-items-end me-8">
            <span class="badge bg-primary-soft text-primary rounded-pill px-12 py-6 fw-bold text-uppercase letter-spacing-wider" style="font-size: 0.65rem;">
              Supplier Portal
            </span>
            <span v-if="supplierName" class="badge bg-light text-muted rounded-pill px-10 py-4 fw-bold mt-4 border shadow-sm" style="font-size: 0.75rem;">
              <v-icon icon="location" size="xs" class="me-4" />
              {{ supplierName }}
            </span>
          </div>
          
          <div class="dropdown">
            <button class="btn btn-icon rounded-circle shadow-none p-0" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              <v-icon icon="user" size="xl" />
            </button>
            <ul class="dropdown-menu dropdown-menu-end shadow border-0 mt-8 py-8">
              <li class="px-16 py-8 border-bottom mb-8">
                <div v-if="supplierName" class="fw-bold text-dark text-sm">{{ supplierName }}</div>
                <div class="text-xs text-muted">{{ user?.email }}</div>
                <div class="text-xs text-muted">{{ user?.name }}</div>
              </li>
              <li class="d-md-none">
                <router-link :to="{ name: 'supplierPortal.upcoming', query: $route.query }" class="dropdown-item d-flex align-items-center py-8 px-16">
                  <v-icon icon="calendar" size="sm" class="me-12" />
                  <span>{{ $t('supplier.portal.upcoming') }}</span>
                </router-link>
              </li>
              <li class="d-md-none">
                <router-link :to="{ name: 'supplierPortal.past', query: $route.query }" class="dropdown-item d-flex align-items-center py-8 px-16">
                  <v-icon icon="calendar" size="sm" class="me-12" />
                  <span>{{ $t('supplier.portal.past') }}</span>
                </router-link>
              </li>
              <li v-if="isAdmin" class="d-md-none">
                <a :href="$router.resolve({ name: 'supplier.index' }).href" class="dropdown-item d-flex align-items-center py-8 px-16">
                  <v-icon icon="arrow-left" size="sm" class="me-12" />
                  <span>Back to Admin</span>
                </a>
              </li>
              <li class="d-md-none border-bottom my-8"></li>
              <li>
                <a class="dropdown-item d-flex align-items-center py-8 px-16 text-danger" href="#" @click.prevent="logout">
                  <v-icon icon="logout" size="sm" class="me-12" />
                  <span>{{ $t('general.menu.logout') }}</span>
                </a>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import TheImpersonateStop from '@/components/header/TheImpersonateStop.vue';

import api from '@/api';

export default defineComponent({
  name: 'TheSupplierHeader',
  components: {
    VIcon,
    TheImpersonateStop,
  },
  data() {
    return {
      fetchedSupplier: null as any,
    };
  },
  computed: {
    ...mapState(useAuthStore, ['user', 'agency']),
    supplierName() {
      if (this.fetchedSupplier?.name) return this.fetchedSupplier.name;
      if (this.agency?.name && !this.isAdmin) return this.agency.name;
      
      return (this.user as any)?.supplier?.name || '';
    },
    isAdmin() {
      return (this.user as any)?.role === 'admin' || (this.user as any)?.roles?.some((r: any) => r.name === 'admin');
    }
  },
  watch: {
    '$route.query.supplier_id': {
      async handler(val) {
        if (val) {
          await this.fetchSupplierInfo(Number(val));
        } else {
          this.fetchedSupplier = null;
        }
      },
      immediate: true,
    }
  },
  methods: {
    async logout() {
      const authStore = useAuthStore();
      try {
        await authStore.logout();
      } finally {
        await this.$router.replace({ name: 'login' });
      }
    },
    async fetchSupplierInfo(supplierId: number) {
      try {
        const supplier = await api.supplier.retrieve(supplierId);
        if (supplier) {
          this.fetchedSupplier = supplier;

          if (!this.agency && !this.isAdmin) {
            const authStore = useAuthStore();
            authStore.setAgency(supplier as any);
          }
        }
      } catch (error) {
        console.error('Failed to fetch supplier info:', error);
      }
    }
  },
  async mounted() {
    // If we don't have a supplier_id in query but we have one in user object
    if (!this.$route.query.supplier_id && (this.user as any)?.supplierId) {
      await this.fetchSupplierInfo(Number((this.user as any).supplierId));
    }
  }
});
</script>

<style lang="scss" scoped>
#top {
  background: white;
  z-index: 1030;
}

.bg-primary-soft { background-color: rgba(13, 110, 253, 0.1); }
.letter-spacing-wider { letter-spacing: 0.05em; }

.dropdown-item {
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f8f9fa;
  }
}

.dropdown-menu {
  border-radius: 12px;
  min-width: 200px;
}
</style>
